import { type RefObject, useLayoutEffect, useRef } from "react";

const CATALOG_LAYOUT_TRANSITION_DURATION_MS = 220;

export interface CatalogLayoutPosition {
  left: number;
  top: number;
}

export interface CatalogLayoutTranslation {
  x: number;
  y: number;
}

/** 更新前の見た目を起点にするためのFLIP移動量を求める。 */
export function getCatalogLayoutTranslation(
  previous: CatalogLayoutPosition | undefined,
  current: CatalogLayoutPosition,
): CatalogLayoutTranslation | undefined {
  if (!previous) return undefined;

  const x = previous.left - current.left;
  const y = previous.top - current.top;
  if (x === 0 && y === 0) return undefined;

  return { x, y };
}

/**
 * 再取得による並び替えを、レイアウトジャンプではなくtransformアニメーションへ
 * 置き換える。DOM順序と読み上げ順序は最新の並びを即座に反映する。
 */
export function useCatalogLayoutTransition<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
): void {
  const previousPositionsRef = useRef(new Map<string, CatalogLayoutPosition>());

  // childrenの順序だけが変わるrenderも検出するため、依存配列なしで毎commit
  // 計測する。読み書きをそれぞれ1巡にまとめ、前回Mapは都度置き換える。
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const items = container.querySelectorAll<HTMLElement>(
      "[data-catalog-layout-key]",
    );
    const currentPositions = new Map<string, CatalogLayoutPosition>();
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    for (const item of items) {
      const key = item.dataset.catalogLayoutKey;
      if (!key) continue;

      const current = { left: item.offsetLeft, top: item.offsetTop };
      currentPositions.set(key, current);

      if (prefersReducedMotion || typeof item.animate !== "function") continue;

      const translation = getCatalogLayoutTranslation(
        previousPositionsRef.current.get(key),
        current,
      );
      if (!translation) continue;

      item.animate(
        [
          {
            transform: `translate(${translation.x}px, ${translation.y}px)`,
          },
          { transform: "translate(0, 0)" },
        ],
        {
          duration: CATALOG_LAYOUT_TRANSITION_DURATION_MS,
          easing: "ease-out",
        },
      );
    }

    previousPositionsRef.current = currentPositions;
  });
}
